import { Button, FormAutocomplete, SaveOverlay, TooltipShortcut } from "@itk/mui-components";
import { OptionalFormController, required, useForm } from "@itk/react-form";
import { Avatar, Badge, Box, FormLabel, Stack, TextField, useTheme } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { hasValue } from "@itk/typescript-utils";
import { profileSettingsPageGeneralSectionK, useProfileSettingsPageGeneralSectionT } from "src/translation-hooks/profile-settings-page-general-section";
import { DekoxIcon } from "src/components";
import { useEffectAfterMount } from "src/utils/hooks/useEffectAfterMount";
import { Shared_DateFormats, Shared_Languages, Shared_PictureResolutions, Shared_Units } from "src/backend/openapi/v1";
import { enumToObject } from "src/utils";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { applicationActions } from "src/components/application-store";
import { ProfileSettingsForm } from "./profile-settings-types";
import { ProfilePictureUploadDialog } from "./components";
import { profileSettingsActions as profileSettingsActions } from "./profile-settings-store";
import { ImageGalleryModal } from "src/components/image-gallery-modal";
import { useDateFormatsTranslation } from "src/translations-enums-hooks/useDateFormatsTranslation";
import { useUnitsTranslation } from "src/translations-enums-hooks/useUnitsTranslation";
import { useLanguagesTranslation } from "src/translations-enums-hooks/useLanguagesTranslation";

const mapStateToProps = (state: RootState) => ({
  contactPersonDetails: state.generalReducer.applicationReducer.contactPersonDetails,
  isProfilePictureDialogOpen: state.pageReducer.profileReducer.profileSettingsReducer.isProfilePictureDialogOpen
});

const ownActions = {
  updateContactPersonDetailsRequest: applicationActions.updateContactPersonDetailsRequest,
  setIsProfilePictureDialogOpen: profileSettingsActions.setIsProfilePictureDialogOpen,
  deleteContactPersonPictureRequest: profileSettingsActions.deleteContactPersonPictureRequest,
  getContactPersonPictureRequest: profileSettingsActions.getContactPersonPictureRequest,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ProfileSettingsBase = (props: Props) => {
  const {
    contactPersonDetails,
    updateContactPersonDetailsRequest,
    isProfilePictureDialogOpen,
    setIsProfilePictureDialogOpen,
    setWarningAction,
    deleteContactPersonPictureRequest,
    getContactPersonPictureRequest
  } = props;

  const profileSettingsPageGeneralSectionT = useProfileSettingsPageGeneralSectionT();
  const theme = useTheme();

  const { getDateFormatTranslation } = useDateFormatsTranslation();
  const { getUnitsTranslation } = useUnitsTranslation();
  const { getLanguagesTranslation } = useLanguagesTranslation();

  const dateFormats = enumToObject<number>(Shared_DateFormats);
  const units = enumToObject<number>(Shared_Units);
  const languages = enumToObject<number>(Shared_Languages);

  const initialData = useMemo((): ProfileSettingsForm => {
    const data: ProfileSettingsForm = { dateFormatId: Object.values(dateFormats)[0], unitId: Object.values(units)[0], languageId: Object.values(languages)[0] };
    if (contactPersonDetails.loading === "has-loaded") {
      if (contactPersonDetails.data.languageId) data.languageId = contactPersonDetails.data.languageId;
      if (contactPersonDetails.data.unitId) data.unitId = contactPersonDetails.data.unitId;
      if (contactPersonDetails.data.dateFormatId) data.dateFormatId = contactPersonDetails.data.dateFormatId;
    }
    return data;
  }, [contactPersonDetails]);

  const initFormController = useMemo(
    (): OptionalFormController<ProfileSettingsForm> => ({
      languageId: { validators: [required] },
      unitId: { validators: [required] },
      dateFormatId: { validators: [required] }
    }),
    []
  );

  const { formState, formController, getValues, validate } = useForm(initialData, initFormController);

  const [previewImage, setPreviewImage] = useState("");

  const profilePicture = useMemo(() => {
    return contactPersonDetails.loading === "has-loaded" ? contactPersonDetails.data.profilePicture : "";
  }, [contactPersonDetails]);

  useEffectAfterMount(() => {
    handleSave();
  }, [JSON.stringify(getValues())]);

  const handleSave = useCallback(() => {
    const errors = validate();
    if (hasValue(errors)) return;

    const values = getValues();
    if (values) updateContactPersonDetailsRequest(values);
  }, [JSON.stringify(getValues())]);

  const handleProfilePictureEdit = useCallback(() => {
    setIsProfilePictureDialogOpen(true);
  }, []);

  const handleProfilePictureDelete = useCallback(() => {
    setWarningAction({
      confirm: () => {
        if (contactPersonDetails.loading === "has-loaded") deleteContactPersonPictureRequest(contactPersonDetails.data.contactPersonId);
        setWarningAction(undefined);
      },
      cancel: () => {
        setWarningAction(undefined);
      },
      variant: "confirmCancel",
      dialogProps: {
        message: profileSettingsPageGeneralSectionT.deleteProfilePictureConfirmation
      }
    });
  }, [contactPersonDetails, profileSettingsPageGeneralSectionT]);

  const handleOpenPreview = useCallback(() => {
    if (contactPersonDetails.loading === "has-loaded" && profilePicture) {
      setPreviewImage(profilePicture);

      getContactPersonPictureRequest(contactPersonDetails.data.contactPersonId, Shared_PictureResolutions.Large, (isSuccess, picture) => {
        if (isSuccess && picture) {
          setPreviewImage(picture);
        }
      });
    }
  }, [profilePicture]);

  return (
    <Stack gap={3}>
      {/* Profile picture */}
      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <DekoxIcon icon="account" sx={{ color: theme.palette.customColors.darkGrey }} />
          <FormLabel component="legend" sx={{ m: 0, fontWeight: theme.typography.body2.fontWeight, fontSize: theme.typography.body2.fontSize }}>
            {profileSettingsPageGeneralSectionT.profilePicture}
          </FormLabel>
        </Stack>
        <Stack sx={{ ml: 4 }}>
          <Box ml={4}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <DekoxIcon
                  icon="edit"
                  fontSize="medium"
                  sx={{ p: 0.5, backgroundColor: theme.palette.customColors.white, borderRadius: "50%", cursor: "pointer", border: `1px solid ${theme.palette.customColors.border}` }}
                  onClick={handleProfilePictureEdit}
                />
              }
            >
              <Avatar
                src={profilePicture}
                sx={{
                  width: 100,
                  height: 100,
                  backgroundColor: theme.palette.primary.dark,
                  cursor: profilePicture ? "pointer" : "default"
                }}
                onClick={handleOpenPreview}
              >
                {contactPersonDetails.loading === "has-loaded" && contactPersonDetails.data.fullName.charAt(0)}
              </Avatar>
            </Badge>
          </Box>
          {profilePicture && (
            <Box>
              <TooltipShortcut shortcut={{ action: handleProfilePictureDelete, shortCutKey: "d", modifier: "alt" }}>
                <Button
                  field="deleteProfilePicture"
                  k={profileSettingsPageGeneralSectionK}
                  t={profileSettingsPageGeneralSectionT}
                  variant="contained"
                  startIcon={<DekoxIcon icon="delete" />}
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    handleProfilePictureDelete();
                  }}
                />
              </TooltipShortcut>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Language */}
      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <DekoxIcon icon="googleTranslate" sx={{ color: theme.palette.customColors.darkGrey }} />
          <FormLabel component="legend" sx={{ m: 0, fontWeight: theme.typography.body2.fontWeight, fontSize: theme.typography.body2.fontSize }}>
            {profileSettingsPageGeneralSectionT.language}
          </FormLabel>
        </Stack>

        <FormAutocomplete
          t={profileSettingsPageGeneralSectionT}
          k={profileSettingsPageGeneralSectionK}
          field="language"
          formFieldState={formState.languageId}
          formFieldController={formController.languageId}
          options={Object.entries(languages)
            .filter(([_, value]) => value !== Shared_Languages.German)
            .map(([_, value]) => ({ description: getLanguagesTranslation(value), value }))
            .sort((x, y) => x.description.localeCompare(y.description))}
          getOptionLabel={option => option.description}
          getId={option => option.value}
          size="small"
          renderInput={params => (
            <TextField
              {...params}
              placeholder={profileSettingsPageGeneralSectionT.language}
              error={!!formState.languageId.error}
              helperText={formState.languageId.error}
              variant="outlined"
              fullWidth
              sx={{ pl: 4, width: 250 }}
            />
          )}
        />
      </Box>

      {/* Date format */}
      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <DekoxIcon icon="calendar" sx={{ color: theme.palette.customColors.darkGrey }} />
          <FormLabel component="legend" sx={{ fontWeight: theme.typography.body2.fontWeight, fontSize: theme.typography.body2.fontSize }}>
            {profileSettingsPageGeneralSectionT.dateFormat}
          </FormLabel>
        </Stack>
        <FormAutocomplete
          t={profileSettingsPageGeneralSectionT}
          k={profileSettingsPageGeneralSectionK}
          field="dateFormat"
          formFieldState={formState.dateFormatId}
          formFieldController={formController.dateFormatId}
          options={Object.entries(dateFormats)
            .map(([_, value]) => ({ description: getDateFormatTranslation(value), value }))
            .sort((x, y) => x.description.localeCompare(y.description))}
          getOptionLabel={option => option.description}
          getId={option => option.value}
          size="small"
          renderInput={params => (
            <TextField
              {...params}
              placeholder={profileSettingsPageGeneralSectionT.dateFormat}
              variant="outlined"
              fullWidth
              error={!!formState.dateFormatId.error}
              helperText={formState.dateFormatId.error}
              sx={{ pl: 4, width: 250 }}
            />
          )}
        />
      </Box>

      {/* Size unit */}
      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <DekoxIcon icon="units" sx={{ color: theme.palette.customColors.darkGrey }} />
          <FormLabel component="legend" sx={{ fontWeight: theme.typography.body2.fontWeight, fontSize: theme.typography.body2.fontSize }}>
            {profileSettingsPageGeneralSectionT.units}
          </FormLabel>
        </Stack>
        <FormAutocomplete
          t={profileSettingsPageGeneralSectionT}
          k={profileSettingsPageGeneralSectionK}
          field="units"
          formFieldState={formState.unitId}
          formFieldController={formController.unitId}
          options={Object.entries(units)
            .map(([_, value]) => ({ description: getUnitsTranslation(value), value }))
            .sort((x, y) => x.description.localeCompare(y.description))}
          getOptionLabel={option => option.description}
          getId={option => option.value}
          size="small"
          renderInput={params => (
            <TextField
              {...params}
              placeholder={profileSettingsPageGeneralSectionT.units}
              variant="outlined"
              fullWidth
              error={!!formState.unitId.error}
              helperText={formState.unitId.error}
              sx={{ pl: 4, width: 250 }}
            />
          )}
        />
      </Box>
      {isProfilePictureDialogOpen && <ProfilePictureUploadDialog />}
      <SaveOverlay />
      {previewImage && (
        <ImageGalleryModal
          open
          setOpen={() => {
            setPreviewImage("");
          }}
          currentIndex={0}
          images={[previewImage]}
        />
      )}
    </Stack>
  );
};

export const ProfileSettings = connect(mapStateToProps, ownActions)(ProfileSettingsBase);
